<template>
  <div id="main">
    <div class="nav-container">
      <p class="my-3 pl-2 text-info d-none d-md-block">ERCCIS Data Search</p>
      <Nav />
    </div>
    <div class="home" id="home">
      <div
        class="jumbotron jumbotron-fluid vh-80 background-size pt-5"
        style="background-image: url('/img/header.jpg');"
      >
        <div
          class="container h-100 d-flex flex-column justify-content-center align-items-center mt-md-5"
        >
          <h1 class="h1 text-white font-weight-bold">
            The Environmental Records Centre for Cornwall and the Isles of
            Scilly Data Search Portal
          </h1>
          <p class="lead text-white">
            Welcome to ERCCIS Data Search. This data search portal is designed
            to provide comprehensive species, habitat and designated sites
            information for Environmental Professionals working in Cornwall and
            the Isles of Scilly.
          </p>
          <div class="d-none d-md-inline">
            <router-link
              v-if="authenticated"
              class="btn btn-primary"
              :to="{ name: 'report-create' }"
              >Create Report</router-link
            >
            <ArrowLink v-else class="mt-5" text="Sign up now" to="#sign-up" />
          </div>
        </div>
      </div>
      <div class="container" id="benefits">
        <div class="jumbotron bg-white mb-0">
          <div class="row align-items-center mt-5 mt-md-0">
            <div class="col-lg-8">
              <h2 class="h1">Why use ERCCIS Data Search?</h2>
            </div>
            <div class="col text-lg-right">
              <router-link
                v-if="authenticated"
                class="btn btn-primary"
                :to="{ name: 'report-create' }"
                >Create Report</router-link
              >
              <ArrowLink v-else text="Sign up now" to="#sign-up" />
            </div>
          </div>
        </div>
      </div>
      <div class="container-fluid p-5">
        <div class="card-group">
          <div class="card text-center p-5 no-border-left no-border-top">
            <TimeIcon class="card-img-top mx-auto my-3" />
            <div class="card-body pt-1">
              <h5 class="card-title">Access all day, everyday</h5>
            </div>
          </div>
          <div class="card text-center p-5 no-border-top">
            <BirdIcon class="card-img-top mx-auto my-3" />
            <div class="card-body pt-1">
              <h5 class="card-title">
                Access to the records of notable species held at ERCCIS,
                including the Species of Conservation Concern (SoCC) listed by
                the JNCC
              </h5>
            </div>
          </div>
          <div class="card text-center p-5 no-border-top no-border-right">
            <CardIcon class="card-img-top mx-auto my-3" />
            <div class="card-body pt-1">
              <h5 class="card-title">Six fixed prices for searches</h5>
            </div>
          </div>
        </div>
        <div class="card-group">
          <div
            class="card text-center p-5 no-border-top no-border-left no-border-bottom"
          >
            <MapIcon class="card-img-top mx-auto my-3" />
            <div class="card-body pt-1">
              <h5 class="card-title">
                Details of all statutory and non-statutory conservation designations
              </h5>
            </div>
          </div>
          <div class="card text-center p-5 no-border-top no-border-bottom">
            <ReportIcon class="card-img-top mx-auto my-3" />
            <div class="card-body pt-1">
              <h5 class="card-title">
                Detailed PDF report and filterable Excel spreadsheet of results
              </h5>
            </div>
          </div>
          <div
            class="card text-center p-5 no-border-top no-border-right no-border-bottom"
          >
            <TreesIcon class="card-img-top mx-auto my-3" />
            <div class="card-body pt-1">
              <h5 class="card-title">
                Comprehensive habitat data (Phase One; Priority Habitats; Land
                Cover) for each search
              </h5>
            </div>
          </div>
        </div>
      </div>
      <div
        class="container-fluid bg-light background-size"
        style="background-image: url('/img/report-look.png');"
        id="example-results"
      >
        <div class="container pt-5">
          <div class="jumbotron bg-transparent pb-0 pt-5">
            <div class="row align-items-center">
              <div class="col-lg-6 mb-5 mb-lg-0">
                <h2 class="h1">What will I receive?</h2>
                <p class="pt-4">
                  A comprehensive report with the latest verified information
                  ERCCIS hosts on the species, habitats and sites in your
                  searched area
                </p>
                <p>
                  Emailed PDF report and a filterable spreadsheet of your
                  results
                </p>
                <p>24/7 access to previous searches for 6 months</p>
                <ArrowLink
                  class="mt-3"
                  text="Download an example report"
                  to="https://cornwall-wildlife-trust.azurewebsites.net/static/ERCCIS_Demo_Report.pdf"
                  target="_blank"
                  download="CWT_Demo_Report.pdf"
                />
              </div>
              <div class="col offset-md-1">
                <img src="@/assets/img/example-report.png" class="img-fluid" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="container py-5" id="how-it-works">
        <div class="text-center">
          <span class="text-primary">The process</span>
          <h2 class="h1 mt-3">How does ERCCIS Data Search work?</h2>

          <div class="card-deck pt-5">
            <div class="card no-border">
              <div class="card-number p-2 mx-auto">
                <span class="h2">1</span>
              </div>
              <div class="card-body mt-2">
                <h5 class="card-title">Request Account</h5>
                <p class="card-text">
                  Complete the simple form below to create an account
                </p>
              </div>
            </div>
            <div class="card no-border">
              <div class="card-number p-2 mx-auto">
                <span class="h2">2</span>
              </div>
              <div class="card-body mt-2">
                <h5 class="card-title">Authorisation</h5>
                <p class="card-text">
                  ERCCIS Staff review your account and might get in touch to
                  discuss the information products available
                </p>
              </div>
            </div>
            <div class="card no-border">
              <div class="card-number p-2 mx-auto">
                <span class="h2">3</span>
              </div>
              <div class="card-body mt-2">
                <h5 class="card-title">Start Searching</h5>
                <p class="card-text">
                  You’ll receive an email allowing you to access the system and
                  start your search
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="container-fluid bg-light-blue background-size py-0 py-md-5"
        style="background-image: url('/img/cost.png');"
        id="pricing"
      >
        <div class="container-fluid text-center py-5">
          <h2 class="h1 mt-3">
            How much does an ERCCIS Data Search report cost?
          </h2>
          <p class="lead pt-4">
            There are {{ reports.length }} fixed costs for the three possible
            search radius available.
          </p>

          <div class="row py-5 px-lg-5" v-if="reports.length > 0">
            <div
              class="col-md-6 col-lg-4 my-3 mb-lg-0"
              v-for="report in reports"
              v-bind:key="report.ReportTypeId"
            >
              <div class="card no-border">
                <img
                  :src="'/img/card-header-' + report.SearchRadius + 'km.jpg'"
                  class="card-img-top"
                  alt="..."
                />
                <div class="card-img-overlay">
                  <h5 class="h1 text-white m-0 pt-2">
                    {{ report.SearchRadius }}km
                  </h5>
                  <p class="card-text text-white font-weight-bold m-0">
                    {{ report.Name }}
                  </p>
                  <p class="card-text text-white">{{ report.Description }}</p>
                </div>
                <div class="card-body mt-4 mb-2">
                  <h5 class="h1 font-weight-normal">
                    &pound;{{ (report.ListPrice / 1.2).toFixed(2) }}
                  </h5>
                  <p class="card-text">
                    &pound;{{ report.ListPrice.toFixed(2) }} inc VAT
                  </p>
                </div>
                <div
                  class="card-footer bg-transparent no-border position-relative"
                >
                  <router-link
                    v-if="authenticated"
                    class="btn btn-primary"
                    :to="{ name: 'report-create' }"
                    >Create Report</router-link
                  >
                  <a href="#sign-up" v-else class="btn btn-primary"
                    >Sign up and get started</a
                  >
                </div>
              </div>
            </div>
          </div>
          <div class="py-5 d-flex justify-content-center" v-else>
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading report types...</span>
            </div>
          </div>
        </div>
      </div>
      <div
        class="container-fluid bg-primary-light background-size"
        id="sign-up"
        style="background-image: url('/img/free.png');"
      >
        <div class="container py-5">
          <div class="row align-items-center">
            <div class="col-md-2 text-center text-md-left mb-4 mb-md-0">
              <Logo />
            </div>
            <div class="col-md-3 mb-3 mb-md-0">
              <p class="text-white text-bold m-0" style="font-size: 1.25rem;">
                <span class="text-underline">No charge</span> if your
                organisation already has an agreement (valid SLA) with ERCCIS
              </p>
            </div>
            <div class="col offset-md-1 mb-3 mb-md-0">
              <p class="text-white m-0" style="font-size: 1.25rem;">
                If your requirements are not listed above, you might need a
                <a
                  class="text-white text-underline"
                  href="https://erccis.org.uk/requesting-data"
                  target="_blank"
                  rel="nofollow"
                  >bespoke Data Search</a
                >. The ERCCIS team would be very happy to discuss your needs
                (Email:
                <a
                  class="text-white text-underline"
                  href="mailto:WIS@Cornwallwildlifetrust.org.uk"
                  >WIS@Cornwallwildlifetrust.org.uk</a
                >
                or Tel: 01872 302250).
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="container-thin py-5" v-if="!authenticated">
        <div class="text-center">
          <h2 class="h1">Sign up today!</h2>
        </div>
        <form class="pt-5" novalidate @submit="checkForm">
          <div class="row">
            <div class="form-group col-md-12 col-lg-2">
              <select
                class="form-control"
                id="Title"
                name="Title"
                v-model="signUp.Title"
              >
                <option value="error">Title *</option>
                <option
                  v-for="title in titles"
                  v-bind:key="title.Id"
                  v-bind:value="title.Id"
                >
                  {{ title.Title }}
                </option>
              </select>
            </div>
            <div class="form-group col-md-6 col-lg-5">
              <input
                type="text"
                class="form-control"
                id="FirstName"
                placeholder="First name *"
                required
                v-model="signUp.FirstName"
              />
            </div>
            <div class="form-group col-md-6 col-lg-5">
              <input
                type="text"
                class="form-control"
                id="LastName"
                placeholder="Last name *"
                required
                v-model="signUp.LastName"
              />
            </div>
          </div>
          <div class="row">
            <div class="form-group col-md-6">
              <input
                type="tel"
                class="form-control"
                id="TelephoneNumber"
                placeholder="Telephone"
                required
                v-model="signUp.TelephoneNumber"
              />
            </div>
            <div class="form-group col-md-6">
              <input
                type="tel"
                class="form-control"
                id="MobileNumber"
                placeholder="Mobile"
                v-model="signUp.MobileNumber"
              />
            </div>
          </div>
          <div class="row">
            <div class="form-group col-md-6">
              <input
                type="email"
                class="form-control"
                id="Email"
                name="Email"
                placeholder="Email *"
                required
                v-model="signUp.Email"
              />
            </div>
            <div class="form-group col-md-6">
              <input
                type="email"
                class="form-control"
                id="ConfirmEmail"
                name="ConfirmEmail"
                placeholder="Confirm email *"
                required
                v-model="signUp.ConfirmEmail"
              />
            </div>
          </div>
          <div class="row">
            <div class="form-group col-md-6">
              <input
                type="text"
                class="form-control"
                id="Organisation"
                placeholder="Organisation *"
                required
                v-model="signUp.Organisation"
              />
            </div>
            <div class="form-group col-md-6">
              <input
                type="text"
                class="form-control"
                id="Position"
                placeholder="Position *"
                required
                v-model="signUp.Position"
              />
            </div>
          </div>
          <div class="row">
            <div class="form-group col-md-6">
              <input
                type="text"
                class="form-control"
                id="BuildingNameOrNumber"
                placeholder="Building name/number"
                required
                v-model="signUp.BuildingNameOrNumber"
              />
            </div>
            <div class="form-group col-md-6">
              <input
                type="text"
                class="form-control"
                id="Street"
                placeholder="Street"
                required
                v-model="signUp.Street"
              />
            </div>
          </div>
          <div class="row">
            <div class="form-group col-md-6">
              <input
                type="text"
                class="form-control"
                id="City"
                placeholder="City"
                required
                v-model="signUp.City"
              />
            </div>
            <div class="form-group col-md-6">
              <input
                type="text"
                class="form-control"
                id="PostCode"
                placeholder="Postcode"
                required
                v-model="signUp.PostCode"
              />
            </div>
          </div>
          <div class="row">
            <div class="form-group col-md-6">
              <input
                type="password"
                class="form-control"
                id="Password"
                placeholder="Password *"
                required
                v-model="signUp.Password"
              />
            </div>
            <div class="form-group col-md-6">
              <input
                type="password"
                class="form-control"
                id="ConfirmPassword"
                placeholder="Confirm Password *"
                required
                v-model="signUp.ConfirmPassword"
              />
            </div>
          </div>
          <div class="form-group">
            <textarea
              class="form-control"
              id="ReportReason"
              v-model="signUp.ReportReason"
              rows="3"
              placeholder="Reason for purchasing a report *"
              required
            ></textarea>
          </div>

          <div v-if="signUpErrors.length">
            <div
              class="alert alert-danger alert-dismissible fade show"
              role="alert"
              v-for="error in signUpErrors"
              :key="error"
            >
              {{ error }}
              <button
                type="button"
                class="close"
                data-dismiss="alert"
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
          <div v-if="createUserErrors.length">
            <div
              class="alert alert-danger alert-dismissible fade show"
              role="alert"
              v-for="error in createUserErrors"
              :key="error"
            >
              {{ error }}
              <button
                type="button"
                class="close"
                data-dismiss="alert"
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
          <div
            v-if="createUserSuccess"
            class="alert alert-success alert-dismissible fade show"
            role="alert"
          >
            Registration successful!
            <button
              type="button"
              class="close"
              data-dismiss="alert"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <button v-if="loading" class="btn btn-primay btn-block mb-3" disabled>
            Loading...
          </button>
          <button v-else type="submit" class="btn btn-primary btn-block mb-3">
            Sign up now
          </button>
        </form>
      </div>
      <!-- Login Modal -->
      <div
        class="modal fade"
        id="login-modal"
        tabindex="-1"
        role="dialog"
        aria-labelledby="login-modal-label"
        aria-hidden="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header no-border">
              <button
                type="button"
                class="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body pt-0">
              <div class="container text-center px-0 px-md-5 pb-5">
                <h2 class="h1">Sign in</h2>
                <p class="lead mb-5">Sign in now and start your search</p>
                <form class="my-3" novalidate @submit="checkLoginForm">
                  <div class="form-group pt-3">
                    <input
                      type="text"
                      class="form-control"
                      id="username"
                      placeholder="Email"
                      v-model="logIn.username"
                    />
                  </div>
                  <div class="form-group pt-3">
                    <input
                      type="password"
                      class="form-control"
                      id="password"
                      placeholder="Password"
                      v-model="logIn.password"
                    />
                  </div>
                  <div v-if="loginErrors.length">
                    <div
                      class="alert alert-danger alert-dismissible fade show"
                      role="alert"
                      v-for="error in loginErrors"
                      :key="error"
                    >
                      {{ error }}
                      <button
                        type="button"
                        class="close"
                        data-dismiss="alert"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                  </div>
                  <button
                    v-if="loading"
                    class="btn btn-primay btn-block mt-3"
                    disabled
                  >
                    Loading...
                  </button>
                  <button
                    v-else
                    type="submit"
                    class="btn btn-primary btn-block mt-3"
                  >
                    Sign in
                  </button>
                </form>
                <p>
                  Forgot your password?
                  <a
                    href="#"
                    data-toggle="modal"
                    data-target="#password-reset-modal"
                    data-dismiss="modal"
                    >Click here to reset</a
                  >
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- /Login Modal -->
      <!-- Password Reset Modal -->
      <div
        class="modal fade"
        id="password-reset-modal"
        tabindex="-1"
        role="dialog"
        aria-hidden="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header no-border">
              <button
                type="button"
                class="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body pt-0">
              <div class="container text-center px-0 px-md-5 pb-5">
                <h2 class="h1">Reset Password</h2>
                <p class="lead mb-5">
                  Enter the email you use to log in and we'll send you an email
                  with a link to reset your password.
                </p>
                <form class="my-3" novalidate @submit="checkPasswordResetForm">
                  <div class="form-group pt-3">
                    <input
                      type="text"
                      class="form-control"
                      id="username"
                      placeholder="Email"
                      v-model="paswordReset.Email"
                    />
                  </div>
                  <div v-if="passwordResetErrors.length">
                    <div
                      class="alert alert-danger alert-dismissible fade show"
                      role="alert"
                      v-for="error in passwordResetErrors"
                      :key="error"
                    >
                      {{ error }}
                      <button
                        type="button"
                        class="close"
                        data-dismiss="alert"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                  </div>
                  <div v-if="passwordResetFormErrors.length">
                    <div
                      class="alert alert-danger alert-dismissible fade show"
                      role="alert"
                      v-for="error in passwordResetFormErrors"
                      :key="error"
                    >
                      {{ error }}
                      <button
                        type="button"
                        class="close"
                        data-dismiss="alert"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                  </div>
                  <div
                    v-if="passwordResetSuccess"
                    class="alert alert-success alert-dismissible fade show"
                    role="alert"
                  >
                    Password reset email sent successully!
                    <button
                      type="button"
                      class="close"
                      data-dismiss="alert"
                      aria-label="Close"
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                  <button
                    v-if="loading"
                    class="btn btn-primay btn-block mt-3"
                    disabled
                  >
                    Loading...
                  </button>
                  <button
                    v-else
                    type="submit"
                    class="btn btn-primary btn-block mt-3"
                  >
                    Reset Password
                  </button>
                </form>
                <p>
                  <a
                    href="#"
                    data-toggle="modal"
                    data-target="#login-modal"
                    data-dismiss="modal"
                    >Log in</a
                  >
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- /Password Reset Modal -->
    </div>
    <Footer />
  </div>
</template>

<script>
// @ is an alias to /src
import ArrowLink from "@/components/ArrowLink.vue";
import Logo from "@/components/icons/Logo.vue";
import BirdIcon from "@/components/icons/Bird.vue";
import TimeIcon from "@/components/icons/Time.vue";
import CardIcon from "@/components/icons/Card.vue";
import MapIcon from "@/components/icons/Map.vue";
import ReportIcon from "@/components/icons/Report.vue";
import TreesIcon from "@/components/icons/Trees.vue";
import Nav from "@/components/Nav.vue";
import Footer from "@/components/Footer.vue";

export default {
  name: "home",
  components: {
    ArrowLink,
    Logo,
    Nav,
    Footer,
    BirdIcon,
    TimeIcon,
    CardIcon,
    MapIcon,
    ReportIcon,
    TreesIcon
  },

  data() {
    return {
      titles: [
        {
          Title: "Mr",
          Id: 0
        },
        {
          Title: "Mrs",
          Id: 1
        },
        {
          Title: "Miss",
          Id: 2
        },
        {
          Title: "Dr",
          Id: 3
        },
        {
          Title: "Ms",
          Id: 4
        },
        {
          Title: "Prof",
          Id: 5
        },
        {
          Title: "Rev",
          Id: 6
        },
        {
          Title: "Lady",
          Id: 7
        },
        {
          Title: "Sir",
          Id: 8
        },
        {
          Title: "Capt",
          Id: 9
        }
      ],
      signUp: {
        Title: "error"
      },
      logIn: {
        username: "",
        password: ""
      },
      paswordReset: {
        Email: ""
      },
      signUpErrors: [],
      loginErrors: [],
      signUpSuccess: false,
      loginSuccess: false,
      passwordResetFormErrors: []
    };
  },
  mounted() {
    if (
      this.$route.query &&
      (this.$route.query.login === true || this.$route.query.login === "true")
    ) {
      $("#login-modal").modal("show");
    }

    this.$store.dispatch("getReportTypes");
  },
  computed: {
    loading() {
      return this.$store.state.loading;
    },
    reports() {
      return this.$store.getters.reports;
    },
    createUserSuccess() {
      return this.$store.getters.createUserSuccess;
    },
    createUserErrors() {
      return this.$store.getters.createUserErrors;
    },
    passwordResetErrors() {
      return this.$store.getters.passwordResetErrors;
    },
    passwordResetSuccess() {
      return this.$store.getters.passwordResetSuccess;
    },
    authenticated() {
      return this.$auth.isAuthenticated();
    }
  },
  methods: {
    checkForm(e) {
      e.preventDefault();
      this.signUpErrors = [];

      //check required
      let missingInputs = 0;
      for (let formInput of e.target.elements) {
        const value = formInput.value.trim();

        if (
          formInput.nodeName === "INPUT" ||
          formInput.nodeName === "TEXTAREA"
        ) {
          if (formInput.hasAttribute("required")) {
            if (value) {
              formInput.classList.remove("is-invalid");
              formInput.classList.add("is-valid");
            } else {
              formInput.classList.remove("is-valid");
              formInput.classList.add("is-invalid");
              missingInputs++;
            }
          }
        }

        if (formInput.nodeName === "SELECT") {
          if (value === "error") {
            formInput.classList.remove("is-valid");
            formInput.classList.add("is-invalid");
            missingInputs++;
          } else {
            formInput.classList.remove("is-invalid");
            formInput.classList.add("is-valid");
          }
        }
      }

      if (missingInputs > 0) {
        this.signUpErrors.push(
          "You are missing some required fields, please try again"
        );
      } else {
        //Email checks
        if (this.signUp.Email !== this.signUp.ConfirmEmail) {
          let emailInput = document.getElementById("Email");
          let confirmEmailInput = document.getElementById("ConfirmEmail");

          this.signUpErrors.push("Email addresses don't match!");

          emailInput.classList.remove("is-valid");
          emailInput.classList.add("is-invalid");

          confirmEmailInput.classList.remove("is-valid");
          confirmEmailInput.classList.add("is-invalid");
        }

        //Password checks
        if (this.signUp.Password !== this.signUp.ConfirmPassword) {
          let passwordInput = document.getElementById("Password");
          let confirmPasswordInput = document.getElementById("ConfirmPassword");

          this.signUpErrors.push("Passwords don't match!");

          passwordInput.classList.remove("is-valid");
          passwordInput.classList.add("is-invalid");

          confirmPasswordInput.classList.remove("is-valid");
          confirmPasswordInput.classList.add("is-invalid");
        }
      }

      if (this.signUpErrors.length === 0) {
        this.submit();
      }
    },
    async submit() {
      this.$store.dispatch("addUser", this.signUp).then(response => {
        if (response.status === 200) {
          this.logIn.username = this.signUp.Email;
          $("#login-modal").modal("show");
        }
      });
    },
    checkLoginForm(e) {
      e.preventDefault();
      this.loginErrors = [];

      let missingInputs = 0;
      for (let formInput of e.target.elements) {
        const value = formInput.value.trim();

        if (formInput.nodeName === "INPUT") {
          if (value) {
            formInput.classList.remove("is-invalid");
            formInput.classList.add("is-valid");
          } else {
            formInput.classList.remove("is-valid");
            formInput.classList.add("is-invalid");
            missingInputs++;
          }
        }
      }

      if (missingInputs > 0) {
        this.loginErrors.push("Please provide your username and password");
      }

      if (this.loginErrors.length === 0) {
        this.login();
      }
    },
    async login() {
      this.$store.commit("SET_LOADING", true);
      try {
        let loginUser = await this.$auth.loginUser(this.logIn);

        //All good
        if (loginUser.status === 200) {
          this.$auth.saveAccessToken(loginUser.data);
          this.$auth.getUserRole();

          $("#login-modal").modal("hide");

          this.$store.commit("LOGIN_EVENT", {
            authenticated: true,
            user: loginUser.data.userName
          });

          this.$router.push({ name: "report-create" });

          this.$store.commit("SET_LOADING", false);
        }

        //Server generated errors
        if (loginUser.status === 400) {
          this.$store.commit("SET_LOADING", false);
          if (loginUser.data) {
            if (loginUser.data.ModelState) {
              let serverModelErrors = Object.values(
                loginUser.data.ModelState
              ).flat();
              this.loginErrors = this.loginErrors.concat(serverModelErrors);
            } else if (loginUser.data.Message) {
              this.loginErrors.push(
                "Error logging in: " + loginUser.data.Message
              );
            } else if (loginUser.data.error) {
              if (loginUser.data.error_description) {
                this.loginErrors.push(
                  "Error logging in: " + loginUser.data.error_description
                );
              } else {
                this.loginErrors.push(
                  "Error logging in: " + loginUser.data.error
                );
              }
            }
          }
        }

        this.$emit("SET_LOADING", false);
      } catch (error) {
        console.log("Home.vue catch: ", error);
      }
    },
    checkPasswordResetForm(e) {
      e.preventDefault();
      this.passwordResetFormErrors = [];

      let missingInputs = 0;
      for (let formInput of e.target.elements) {
        const value = formInput.value.trim();

        if (formInput.nodeName === "INPUT") {
          if (value) {
            formInput.classList.remove("is-invalid");
            formInput.classList.add("is-valid");
          } else {
            formInput.classList.remove("is-valid");
            formInput.classList.add("is-invalid");
            missingInputs++;
          }
        }
      }

      if (missingInputs > 0) {
        this.passwordResetFormErrors.push("Please provide your email address");
      }

      if (this.passwordResetErrors.length === 0) {
        this.sendPasswordResetEmail();
      }
    },
    sendPasswordResetEmail() {
      this.$store.dispatch("sendPasswordResetEmail", this.paswordReset.Email);
    }
  }
};
</script>

<style scoped lang="scss">
.card-number {
  border: 5px solid #ecedd0;
  width: 60px;
  height: 60px;
}
</style>
